import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiList } from '../api/apiClient';
import { DebugLog, useDebugLog } from '../components/visitor/DebugLog';
import { apiOrigin, visitorOrigin } from '../utils/surface';

const VISITOR_BASE = visitorOrigin();
const API = apiOrigin();

interface PropertyRow {
  id: number;
  name: string;
  property_uuid?: string;
  address?: string;
  city?: string;
  state?: string;
  visitor_enabled?: boolean;
  status?: string;
}

const propKey = (p: PropertyRow) => p.property_uuid || String(p.id);

export const LiveVisitorPages: React.FC = () => {
  const log = useDebugLog();
  const [properties, setProperties] = useState<PropertyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PropertyRow | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    (async () => {
      log.info('Loading properties from api.intrlock.io …');
      try {
        const data = await apiList('properties');
        const list: PropertyRow[] = Array.isArray(data) ? data : (data as any).items || [];
        setProperties(list);
        log.success(`Loaded ${list.length} properties`);
        const enabled = list.filter((p) => p.visitor_enabled !== false).length;
        log.info(`${enabled} have visitor_enabled !== false`);
      } catch (err: any) {
        log.error('Failed to list properties', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((p) =>
      [p.name, p.address, p.city, p.state, p.property_uuid, String(p.id)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [properties, search]);

  const selectProperty = async (p: PropertyRow) => {
    setSelected(p);
    setOpen(false);
    setSearch('');
    setIframeKey((k) => k + 1);
    await runHealthChecks(p);
  };

  const runHealthChecks = async (p: PropertyRow) => {
    log.info(`── Checking visitor services for "${p.name}" (id=${p.id}, uuid=${p.property_uuid || '—'}) ──`);

    // 1. Property fetch
    try {
      const t = Date.now();
      const data = await apiList('properties');
      const list: PropertyRow[] = Array.isArray(data) ? data : (data as any).items || [];
      const match = list.find((x) => x.id === p.id);
      log.success(`GET /properties → ${list.length} rows in ${Date.now() - t}ms${match ? '' : ' (target missing!)'}`);
    } catch (e: any) {
      log.error('GET /properties failed', e);
    }

    // 2. Units endpoint
    try {
      const t = Date.now();
      const r = await fetch(`${API}/visitor/property/${p.id}/units`);
      const ok = r.ok;
      let count = 0;
      try { const j = await r.json(); count = Array.isArray(j) ? j.length : (j.items?.length || 0); } catch {}
      log[ok ? 'success' : 'error'](`GET /visitor/property/${p.id}/units → ${r.status} (${count} units, ${Date.now() - t}ms)`);
    } catch (e: any) {
      log.error('GET /visitor/:id/units failed', e);
    }

    // 3. Public property endpoint carries the visitor-facing settings.
    try {
      const t = Date.now();
      const r = await fetch(`${API}/visitor/property/${p.id}`);
      log[r.ok ? 'success' : 'warn'](`GET /visitor/property/${p.id} → ${r.status} (${Date.now() - t}ms)`);
    } catch (e: any) {
      log.error('GET /visitor/property/:id failed', e);
    }

    // 4. Intercom unlock-pin (OPTIONS preflight check)
    try {
      const t = Date.now();
      const r = await fetch(`${API}/intercom/unlock-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: p.id, pin: '0000' }),
      });
      log[r.status < 500 ? 'success' : 'error'](
        `POST /intercom/unlock-pin (probe) → ${r.status} (${Date.now() - t}ms) — endpoint reachable`,
      );
    } catch (e: any) {
      log.error('POST /intercom/unlock-pin unreachable', e);
    }

    // 5. Face verify endpoint
    try {
      const t = Date.now();
      const r = await fetch(`${API}/face/verify-kiosk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: p.id, image_base64: '' }),
      });
      log[r.status < 500 ? 'success' : 'error'](
        `POST /face/verify-kiosk (probe) → ${r.status} (${Date.now() - t}ms) — endpoint reachable`,
      );
    } catch (e: any) {
      log.error('POST /face/verify-kiosk unreachable', e);
    }

    // 6. Visitor frontend HEAD
    try {
      const t = Date.now();
      const r = await fetch(`${VISITOR_BASE}/${propKey(p)}`, { method: 'HEAD', mode: 'no-cors' });
      log.info(`HEAD ${VISITOR_BASE}/${propKey(p)} → opaque (${Date.now() - t}ms) — page should load in iframe`);
    } catch (e: any) {
      log.warn('HEAD on visitor frontend blocked (CORS/no-cors expected)', e);
    }
  };

  const reload = () => {
    setIframeKey((k) => k + 1);
    if (selected) {
      log.info('Reloading iframe …');
      runHealthChecks(selected);
    }
  };

  return (
    <div className="shadow-page-frame">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Visitor Pages</h1>
          <p className="text-sm text-gray-500 mt-1">
            Live preview of <code>visitor.intrlock.io</code> for any property, with built-in service health checks.
          </p>
        </div>
        {selected && (
          <div className="flex items-center gap-2">
            <button
              onClick={reload}
              className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-gray-700"
            >
              Reload
            </button>
            <a
              href={`${VISITOR_BASE}/${propKey(selected)}`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-sm text-white"
            >
              Open in new tab ↗
            </a>
          </div>
        )}
      </div>

      {/* Property selector */}
      <div ref={dropdownRef} className="relative mb-4 max-w-lg">
        <button
          onClick={() => setOpen((v) => !v)}
          disabled={loading}
          className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-left text-sm hover:border-gray-400 disabled:opacity-50"
        >
          <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
            {loading
              ? 'Loading properties…'
              : selected
                ? `${selected.name}${selected.address ? ` — ${selected.address}` : ''}`
                : 'Select a property…'}
          </span>
          <svg className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute left-0 right-0 mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, city, UUID, or ID…"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div className="max-h-72 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">No matches</div>
              ) : (
                filtered.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => selectProperty(p)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0"
                  >
                    <div className="text-sm font-medium text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {[p.address, p.city, p.state].filter(Boolean).join(', ') || '—'}
                      {' · '}
                      <code>{propKey(p)}</code>
                      {p.visitor_enabled === false && <span className="ml-2 text-amber-600">visitor disabled</span>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Iframe + debug log */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
        <div className="bg-gray-100 rounded-xl border border-gray-200 overflow-hidden" style={{ minHeight: 700 }}>
          {selected ? (
            <iframe
              key={iframeKey}
              ref={iframeRef}
              title={`Visitor page — ${selected.name}`}
              src={`${VISITOR_BASE}/${propKey(selected)}`}
              className="w-full"
              style={{ height: 760, border: 0, backgroundColor: 'white' }}
              allow="camera; microphone"
              onLoad={() => log.success(`iframe loaded: ${VISITOR_BASE}/${propKey(selected)}`)}
              onError={() => log.error('iframe failed to load')}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-gray-400 py-32">
              Select a property to load its live visitor page.
            </div>
          )}
        </div>

        <DebugLog log={log} title="Service Debug Log" />
      </div>
    </div>
  );
};

export default LiveVisitorPages;
